import React from "react";
import { t, Trans } from "@lingui/macro";
import { Alert } from "components/UI/Alert/Alert";
import { typeDataOrdersRowActions } from "./index";
import { TableBody, TableHeader } from "./tableData";
import { DashboardContentPage } from "../DashboardContentTable";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { InputSearch } from "../../../UI/InputSearch/InputSearch";
import { typeOrdersItemSlim } from "model/dashboardOrders/dOrdersReducer";
import { FiltersDashboard } from "../DashboardContentTable/FiltersDashboard";
import { DataTableDashboard } from "../DashboardContentTable/DataTableDashboard";
import { PopupPanelDashboard } from "../DashboardContentTable/PopupPanelDashboard";
import { PopupAdd } from "components/containers/dashboard/dataOrders/popupContents/popupAdd";
import { DOrdersManagementActions } from "components/containers/dashboard/dataOrders/actions";
import { DOrdersManagementFilters } from "components/containers/dashboard/dataOrders/filters";
import { typeDataProductInfoBulkActions } from "components/containers/dashboard/dataProducts";
import { PopupEdit } from "components/containers/dashboard/dataOrders/popupContents/popupEdit";
import { PopupDetails } from "components/containers/dashboard/dataOrders/popupContents/popupDetails";
import { PopupBulkCancel } from "components/containers/dashboard/dataOrders/popupContents/popupBulkCancel";
import { PopupChangeStatus } from "components/containers/dashboard/dataOrders/popupContents/popupChangeStatus";
import { typePaginationDashboardObject } from "../DashboardContentTable/PaginationDashboard/PaginationDashboard";
import { ConfirmDialogDashboard } from "components/containers/dashboard/DashboardContentTable/ConfirmDialogDashboard";
import { PopupBulkChangeStatus } from "components/containers/dashboard/dataOrders/popupContents/popupBulkChangeStatus";

// Type
export type typeViewDataOrders = {
  variant?: string;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;

  openFilterBar: boolean;
  setOpenFilterBar: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupDetails: boolean;
  setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupAdd: boolean;
  setOpenPopupAdd: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupEdit: boolean;
  setOpenPopupEdit: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupChangeStatus: boolean;
  setOpenPopupChangeStatus: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupBulkCancel: boolean;
  setOpenPopupBulkCancel: React.Dispatch<React.SetStateAction<boolean>>;

  openPopupBulkChangeStatus: boolean;
  setOpenPopupBulkChangeStatus: React.Dispatch<React.SetStateAction<boolean>>;

  openCancelDialog: boolean;
  setOpenCancelDialog: React.Dispatch<React.SetStateAction<boolean>>;

  onClickExport: () => void;

  onCancelConfirmHandler: () => void;
  onCancelCancelHandler: () => void;

  searchValue: string;
  onChangeSearchValue: (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => void;
  onEnterSearch: () => void;
  onClearSearch: () => void;

  rowsActions: typeDataOrdersRowActions;
  bulkActions: typeDataProductInfoBulkActions;

  currentItem: typeOrdersItemSlim | undefined;

  pagination?: typePaginationDashboardObject;
  isActiveFilters: boolean;
  setIsActiveFilters: React.Dispatch<React.SetStateAction<boolean>>;
};
// Component
export const ViewDataOrders: React.FC<typeViewDataOrders> = ({
  variant,
  setSidebarOpen,
  openFilterBar,
  setOpenFilterBar,

  openPopupDetails,
  setOpenPopupDetails,
  openPopupAdd,
  setOpenPopupAdd,
  openPopupEdit,
  setOpenPopupEdit,
  openPopupChangeStatus,
  setOpenPopupChangeStatus,
  openPopupBulkChangeStatus,
  setOpenPopupBulkChangeStatus,
  openPopupBulkCancel,
  setOpenPopupBulkCancel,

  openCancelDialog,
  setOpenCancelDialog,
  onCancelConfirmHandler,
  onCancelCancelHandler,

  searchValue,
  onChangeSearchValue,
  onClearSearch,
  onEnterSearch,

  rowsActions,
  bulkActions,
  currentItem,
  pagination,
  isActiveFilters,
  setIsActiveFilters,
  onClickExport,
}) => {
  return (
    <DashboardContentPage
      headerTitle={<Trans>Orders</Trans>}
      setSidebarOpen={setSidebarOpen}
      actions={
        <DOrdersManagementActions
          onClickExport={onClickExport}
          onClickAdd={() => setOpenPopupAdd(true)}
        />
      }
    >
      {/* Filter bar */}
      <FiltersDashboard
        variant={variant}
        filters={{
          filtersForm: (
            <DOrdersManagementFilters
              setOpenFilterBar={setOpenFilterBar}
              setIsActiveFilters={setIsActiveFilters}
            />
          ),
          openFilterBar: openFilterBar,
          setOpenFilterBar: setOpenFilterBar,
        }}
        searchBar={
          <div>
            <InputSearch
              id="orderNumberSearch"
              name="orderNumberSearch"
              placeholder={t`Search by order's number or external Id`}
              value={searchValue}
              onChange={onChangeSearchValue}
              onClear={onClearSearch}
              onKeyEnterDown={onEnterSearch}
            />
          </div>
        }
        isActiveFilters={isActiveFilters}
      />
      <div className={"flex -mt-5"}>
        <ExclamationCircleIcon className="w-4 h-4 mr-1 text-gray-600" />
        <p className={"text-xs  text-gray-500 text-center"}>
          <Trans>
            Enter the full order number (it consists of numbers only) or part of
            the external identification number and press “Enter”.
          </Trans>
        </p>
      </div>

      <Alert
        type="info"
        title={
          <Trans>
            You cannot edit an order after the order has been delivered to the
            customer.
            <br />
            Orders in the status "DELIVERED", "CANCELLED" or "COMPLETED" are
            available for viewing only.
          </Trans>
        }
      />

      {/* Table data */}
      <DataTableDashboard
        tableHeader={<TableHeader bulkOperations={bulkActions} />}
        tableBody={
          <TableBody
            variant={variant}
            rowsActions={rowsActions}
            setOpenPopupDetails={setOpenPopupDetails}
          />
        }
        pagination={pagination}
      />

      {/* Popup Add item dialog */}
      <PopupPanelDashboard
        openPopup={openPopupAdd}
        setOpenPopup={setOpenPopupAdd}
        title={<Trans>Add order</Trans>}
        popupContainerClassName="h-full md:w-screen"
      >
        <PopupAdd setOpenPopup={setOpenPopupAdd} />
      </PopupPanelDashboard>

      {/* Popup Edite item dialog */}
      <PopupPanelDashboard
        title={<Trans>Edit order : {currentItem?.number}</Trans>}
        openPopup={openPopupEdit}
        setOpenPopup={setOpenPopupEdit}
        popupContainerClassName="h-full md:w-screen"
      >
        <PopupEdit setOpenPopup={setOpenPopupEdit} />
      </PopupPanelDashboard>

      {/* Popup change status item dialog */}
      <PopupPanelDashboard
        title={<Trans>Change status of order : {currentItem?.number}</Trans>}
        openPopup={openPopupChangeStatus}
        setOpenPopup={setOpenPopupChangeStatus}
      >
        <PopupChangeStatus setOpenPopup={setOpenPopupChangeStatus} />
      </PopupPanelDashboard>

      {/* Popup bulk change status item dialog */}
      <PopupPanelDashboard
        title={<Trans>Change status all selected orders?</Trans>}
        openPopup={openPopupBulkChangeStatus}
        setOpenPopup={setOpenPopupBulkChangeStatus}
      >
        <PopupBulkChangeStatus setOpenPopup={setOpenPopupBulkChangeStatus} />
      </PopupPanelDashboard>

      {/* Popup bulk cancel item dialog */}
      <PopupPanelDashboard
        title={<Trans>Cancel all selected orders?</Trans>}
        openPopup={openPopupBulkCancel}
        setOpenPopup={setOpenPopupBulkCancel}
      >
        <PopupBulkCancel setOpenPopup={setOpenPopupBulkCancel} />
      </PopupPanelDashboard>

      {/* Item Cancel dialog */}
      <ConfirmDialogDashboard
        variant={variant}
        type="error"
        title={<Trans>Cancel order</Trans>}
        message={
          <Trans>
            Are you sure you want to cancel this order{" "}
            <b>{currentItem?.number}</b>?
          </Trans>
        }
        primaryButton={{
          title: <Trans>Yes, cancel</Trans>,
          action: onCancelConfirmHandler,
        }}
        secondaryButton={{
          title: <Trans>No, don't cancel </Trans>,
          action: onCancelCancelHandler,
        }}
        openDialog={openCancelDialog}
        setOpenDialog={setOpenCancelDialog}
      />

      {/* Popup Details */}
      <PopupPanelDashboard
        title={<Trans>Details</Trans>}
        openPopup={openPopupDetails}
        setOpenPopup={setOpenPopupDetails}
      >
        <PopupDetails
          setOpenPopup={setOpenPopupDetails}
          setOpenEditPopup={setOpenPopupEdit}
        />
      </PopupPanelDashboard>
    </DashboardContentPage>
  );
};
